import React from 'react';
import { MetricPointPayload } from './interfaces';
import './theme.css';

interface LiveMetricChartProps {
  /** Points received so far, in arrival order. Rendered as a simple SVG line chart -- no charting dependency for this first pass. */
  points: MetricPointPayload[];
}

interface MetricSeriesChartProps {
  metricName: string;
  series: MetricPointPayload[];
}

const CHART_WIDTH = 300;
const CHART_HEIGHT = 90;

/**
 * Live training metric chart matching the approved design mockup
 * (smartwater-mockup-live.html): a dark "chart-box" card with a glowing
 * sparkline line and a pulsing endpoint dot. Renders `metric_point` events
 * as a running line, one series (chart-box) per distinct `metric_name`.
 * Plain SVG rather than a charting library -- this dashboard has exactly
 * one simple series-over-time need, so a dependency isn't justified yet.
 */
export function LiveMetricChart({ points }: LiveMetricChartProps) {
  if (points.length === 0) {
    return (
      <div className="chart-box">
        <div className="chart-title">
          <span>No metrics received yet</span>
        </div>
      </div>
    );
  }

  const seriesByName = new Map<string, MetricPointPayload[]>();
  for (const point of points) {
    const series = seriesByName.get(point.metric_name) ?? [];
    series.push(point);
    seriesByName.set(point.metric_name, series);
  }

  return (
    <div className="metric-panel">
      {[...seriesByName.entries()].map(([metricName, series]) => (
        <MetricSeriesChart key={metricName} metricName={metricName} series={series} />
      ))}
    </div>
  );
}

function MetricSeriesChart({ metricName, series }: MetricSeriesChartProps) {
  const values = series.map((point) => point.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  // Guard against a zero-height chart when every point so far has the same
  // value (e.g. only one point received) -- a real, not hypothetical, case
  // early in a run.
  const valueRange = maxValue - minValue || 1;

  const toX = (index: number) =>
    series.length === 1 ? 0 : (index / (series.length - 1)) * CHART_WIDTH;
  const toY = (value: number) => CHART_HEIGHT - ((value - minValue) / valueRange) * CHART_HEIGHT;

  const linePoints = series.map((point, index) => `${toX(index)},${toY(point.value)}`).join(' ');
  const latestValue = values[values.length - 1];
  const lastIndex = series.length - 1;

  return (
    <div className="chart-box">
      <div className="chart-title">
        <span>{metricName}</span>
        <span className="val">{latestValue.toFixed(4)}</span>
      </div>
      <svg
        className="sparkline"
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${metricName} over time`}
      >
        <line className="gridline" x1={0} y1={CHART_HEIGHT * 0.25} x2={CHART_WIDTH} y2={CHART_HEIGHT * 0.25} />
        <line className="gridline" x1={0} y1={CHART_HEIGHT * 0.5} x2={CHART_WIDTH} y2={CHART_HEIGHT * 0.5} />
        <line className="gridline" x1={0} y1={CHART_HEIGHT * 0.75} x2={CHART_WIDTH} y2={CHART_HEIGHT * 0.75} />
        <polyline className="line" points={linePoints} />
        <circle className="glow-point" r={3.5} cx={toX(lastIndex)} cy={toY(values[lastIndex])} />
      </svg>
    </div>
  );
}
